/*----------------------------------
- GENERATED FILE
----------------------------------*/

// This file is generated by Proteum from client/pages/marketing/affiliate-program.tsx.
// Do not edit it manually.

import type __GeneratedRouteApp from "@/client/index";

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';
import { ArrowRight, CheckCircle, Gift, Target, TrendingUp, Users, Zap } from 'lucide-preact';

/*----------------------------------
- APP
----------------------------------*/

import PublicHeader from '@/client/components/marketing/PublicHeader';
import Page from '@/client/pages/_layout/_page';
import { MARKETING_URLS } from '@/client/catalogs/marketing/navigation';

/*----------------------------------
- TYPES
----------------------------------*/

interface AffiliateBenefit {
  icon: typeof Gift;
  title: string;
  description: string;
}
interface AffiliateStep {
  step: string;
  title: string;
  description: string;
}
interface AffiliatePlan {
  name: string;
  icon: string;
  subtitle: string;
  model: string;
  duration: string;
  description: string;
  popular?: boolean;
  features: string[];
  tiers?: Array<{
    range: string;
    label: string;
  }>;
}

/*----------------------------------
- CONSTANTS
----------------------------------*/

const BENEFITS: AffiliateBenefit[] = [{
  icon: Gift,
  title: 'Flexible Commission Models',
  description: 'Choose Boost or Partner per client. Mix both models across your portfolio based on the relationship.'
}, {
  icon: TrendingUp,
  title: 'Optimized Conversion Process',
  description: 'Free trial, then guided discovery, then a tailored activation path. Maximizes conversion and retention.'
}, {
  icon: Users,
  title: 'Scalable Earnings',
  description: 'Partner rate increases as your active client base grows. More clients means stronger recurring upside.'
}, {
  icon: Zap,
  title: 'Fast Payouts',
  description: 'Monthly payouts via bank transfer within 14 days of invoice. Track everything in your Ambassador dashboard.'
}];
const HOW_IT_WORKS: AffiliateStep[] = [{
  step: '1',
  title: 'Apply & Sign',
  description: 'Quick identity verification, then your link is activated immediately'
}, {
  step: '2',
  title: 'Share',
  description: 'We provide ready-to-use content: social posts, email templates, one-pager. Your link tracks automatically'
}, {
  step: '3',
  title: 'Choose Model',
  description: 'Refer a client and choose Boost or Partner. Once confirmed, the model is locked for that client'
}, {
  step: '4',
  title: 'Track & Earn',
  description: 'Real-time dashboard shows active clients, tier status, and commissions. Invoice monthly, get paid within 14 days'
}];
const PLANS: AffiliatePlan[] = [{
  name: 'Boost',
  icon: '⚡',
  subtitle: 'Quick win',
  model: 'Launch commission',
  duration: 'Short-term',
  description: 'Earn a strong launch commission during the first months of each qualified client relationship.',
  features: ['Good for generating immediate cash', 'Perfect for your first referrals', 'Fast return on investment', 'Not available for Heads of Team']
}, {
  name: 'Partner',
  icon: '♾️',
  subtitle: 'Lifetime',
  model: 'Recurring share',
  duration: 'Forever',
  description: 'Earn recurring commission for the lifetime of the client relationship. Terms scale with your active Partner base.',
  popular: true,
  features: ['Builds passive recurring income', 'Terms scale with portfolio size', 'Unlocks Ambassador Network Manager', 'Designed for long-term relationships'],
  tiers: [{
    range: '1-10 active',
    label: 'Starter'
  }, {
    range: '11-20 active',
    label: 'Growth'
  }, {
    range: '21+ active',
    label: 'Scale'
  }]
}];

/*----------------------------------
- CONTROLEUR
----------------------------------*/

export const __register = (app: __GeneratedRouteApp) => {
  const { Router } = app;
  return Router.page('/affiliate-program', { ...({
  static: {
    refresh: '1h',
    urls: ['/affiliate-program']
  }
}), ...{ filepath: "/app/client/pages/marketing/affiliate-program.tsx", sourceLocation: { line: 117, column: 1 }, id: "marketing_affiliate_program" } }, null, ({}) => {
  const [hoveredPlan, setHoveredPlan] = React.useState<string | null>(null);
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <Page withLayout={false}>
            <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-pink-50">
                <PublicHeader />

                <section className="pt-32 pb-20 px-6 lg:px-8">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center">
                            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full mb-6 font-semibold">
                                <Gift size={18} />
                                Affiliate Program
                            </div>
                            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
                                Ambassador Program
                            </h1>
                            <p className="text-xl text-slate-600 mb-4 max-w-3xl mx-auto leading-relaxed">
                                Earn commissions by bringing qualified clients to CrossPath. Share via your unique affiliate link — every confirmed relationship is tracked automatically, invoiced by you, paid by CrossPath.
                            </p>
                            <div className="bg-gradient-to-r from-orange-100 to-pink-100 border border-orange-200 rounded-2xl p-6 max-w-3xl mx-auto mb-8">
                                <p className="font-semibold text-orange-700 text-lg mb-2">Optimized Conversion</p>
                                <p className="text-slate-700 font-medium mb-2">Free trial → guided discovery → tailored activation</p>
                                <p className="text-slate-600 text-sm">Proven process maximizes conversion and client retention for your portfolio.</p>
                            </div>
                            <a href={MARKETING_URLS.login} className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center gap-2">
                                Join the Program
                                <ArrowRight size={20} />
                            </a>
                        </div>
                    </div>
                </section>

                <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-white to-slate-50">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-4">
                                Choose Your Model — Per Client
                            </h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                You choose Boost or Partner at the moment you refer each client. Mix both models freely across your portfolio.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {PLANS.map((plan, index) => <div key={plan.name} onMouseEnter={() => setHoveredPlan(plan.name)} onMouseLeave={() => setHoveredPlan(null)} className={`bg-white rounded-3xl p-6 shadow-xl border-2 ${plan.popular ? 'border-orange-500 md:scale-105' : 'border-slate-200'} hover:shadow-2xl transition-all relative cursor-pointer`}>
                                    {plan.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                            <span className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                                                Most Popular
                                            </span>
                                        </div>}

                                    <div className="text-center mb-4">
                                        <div className="text-5xl mb-2">{plan.icon}</div>
                                        <h3 className="text-2xl font-bold text-slate-900 mb-1">{plan.name}</h3>
                                        <p className="text-slate-600 text-sm font-medium">{plan.subtitle}</p>
                                    </div>

                                    <div className="text-center mb-4 pb-4 border-b border-slate-200">
                                        <div className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent mb-1">
                                            {plan.model}
                                        </div>
                                        <p className="text-slate-600 font-semibold text-sm mb-1">{plan.duration}</p>
                                    </div>

                                    <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                                        {plan.description}
                                    </p>

                                    <ul className="space-y-2 mb-4">
                                        {plan.features.map(feature => <li key={feature} className="flex items-start gap-2">
                                                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                                <span className="text-slate-700 text-sm">{feature}</span>
                                            </li>)}
                                    </ul>
                                        {plan.name === 'Partner' && hoveredPlan === 'Partner' && plan.tiers && <div className="bg-slate-50 rounded-xl p-4 mt-4 overflow-hidden">
                                                <p className="font-semibold text-slate-900 mb-3 text-sm">Partner Tiers</p>
                                                <div className="space-y-2">
                                                    {plan.tiers.map(tier => <div key={tier.range} className="flex items-center justify-between text-xs">
                                                            <span className="text-slate-600">{tier.range}</span>
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-bold text-orange-600">{tier.label}</span>
                                                            </div>
                                                        </div>)}
                                                </div>
                                            </div>}

                                        {plan.name === 'Boost' && hoveredPlan === 'Boost' && <div className="bg-orange-50 rounded-xl p-4 mt-4 overflow-hidden border border-orange-200">
                                                <h4 className="font-semibold text-slate-900 mb-2 text-sm flex items-center gap-2">
                                                    <Target className="w-4 h-4 text-orange-600" />
                                                    How to Think About It
                                                </h4>
                                                <p className="text-slate-700 text-xs leading-relaxed mb-3">
                                                    Use <span className="font-semibold text-orange-600">Boost</span> on your first clients to generate immediate income.
                                                    Use Partner when the relationship is expected to compound over time.
                                                </p>
                                                <div className="bg-white rounded-lg p-3 border border-orange-200">
                                                    <p className="text-xs font-semibold text-orange-600 mb-1">Boost Example</p>
                                                    <p className="text-sm font-bold text-slate-900">Launch-focused commission</p>
                                                    <p className="text-xs text-slate-600">strong upfront incentive, then stops</p>
                                                </div>
                                            </div>}
                                </div>)}
                        </div>
                    </div>
                </section>

                <section className="py-20 px-6 lg:px-8">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-4">
                                How It Works
                            </h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                Start earning in four simple steps
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                            {HOW_IT_WORKS.map((item, index) => <div key={item.step} className="text-center">
                                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-pink-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                                        {item.step}
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                                    <p className="text-slate-600">{item.description}</p>
                                </div>)}
                        </div>
                    </div>
                </section>

                <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-white">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-4">
                                Why Join Our Program
                            </h2>
                            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                                More than just commissions - build a sustainable business
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {BENEFITS.map((benefit, index) => {
              const Icon = benefit.icon;
              return <div key={benefit.title} className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 hover:shadow-2xl transition-all">
                                        <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                                            <Icon className="w-7 h-7 text-white" />
                                        </div>
                                        <h3 className="text-2xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                                        <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
                                    </div>;
            })}
                        </div>
                    </div>
                </section>

                <section className="py-20 px-6 lg:px-8 bg-gradient-to-r from-orange-500 to-pink-500">
                    <div className="max-w-4xl mx-auto text-center">
                        <div>
                            <h2 className="text-4xl font-bold text-white mb-6">
                                Ready to Start Earning?
                            </h2>
                            <p className="text-xl text-white/90 mb-8 leading-relaxed">
                                Top performers can apply to become Ambassador Network Managers — unlock higher commissions and build your own affiliate team.
                            </p>
                            <a href={MARKETING_URLS.login} className="bg-white text-orange-600 hover:bg-slate-50 px-8 py-4 rounded-xl font-semibold text-lg transition-all shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center gap-2">
                                Apply Now
                                <ArrowRight size={20} />
                            </a>
                        </div>
                    </div>
                </section>
            </div>
        </Page>;
});
};
